import {
  useIsLoggedIn,
  useSessionTyped,
  useUserIdWithLoading,
} from '@dreamstack/auth'
import { useLocale, useTranslation } from '@dreamstack/i18n'
import { useTrackFormSubmissionGA4 } from '@dreamstack/tracking'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { AccessFileForm } from '../accessFile/AccessFileForm'
import { getAccessFileUrls } from '../accessFile/getAccessFileUrls'
import type { AccessFileFormData } from '../accessFile/useAccessFileFormSchema'
import { useAccessFileFormSchema } from '../accessFile/useAccessFileFormSchema'
import { useSuccessDialog } from '../confirmation/useSuccessDialog'
import { NewsletterType } from '../newsletter/NewsletterTypes.static'

const baseUrl = process.env.NEXT_PUBLIC_NEXT_BASE_URL

export const HousingCostReportOrderReportForm: FunctionComponent<
  React.PropsWithChildren<{}>
> = () => {
  const yupSchemaResolver = useAccessFileFormSchema()
  const methods = useForm({ resolver: yupSchemaResolver })
  const t = useTranslation()
  const router = useRouter()
  const { userId } = useUserIdWithLoading()
  const locale = useLocale()
  const trackingFormSubmissionForGA4 = useTrackFormSubmissionGA4()
  const { mutateAsync: trackFormSubmissionServer } =
    trpc.anonymousTracking.trackEvent.useMutation()
  const isLoggedIn = useIsLoggedIn()
  const session = useSessionTyped()
  const { mutateAsync: sendHousingCostMail, isPending: loadingHousingCost } =
    trpc.newsletter.sendHousingCostReport.useMutation()

  const {
    mutateAsync: sendNewsletterRegistration,
    isPending: loadingNewsletter,
  } = trpc.newsletter.sendRegisterNewsletter.useMutation()

  const somethingIsLoading = useMemo(() => {
    return loadingHousingCost || loadingNewsletter
  }, [loadingHousingCost, loadingNewsletter])

  const redirectUrl = getAccessFileUrls(locale, 'housingCost', 'redirect')

  const returnToLandingpage = () => {
    router.push(redirectUrl)
  }

  const { SuccessDialog, show } = useSuccessDialog({
    title: t('accentro:housingCostReportForm.modalTitle'),
    text: t('accentro:housingCostReportForm.modalBody'),
    ctaLabel: t('accentro:housingCostReportForm.modalCta'),
    onCtaClick: returnToLandingpage,
  })

  // TODO: Refactor to one place. See other comments.
  const { data: jwtHrefData } = trpc.user.getJwtForOptInAccess.useQuery(
    { type: 'HousingCostReport' },
    { enabled: isLoggedIn, trpc: { ssr: false }, staleTime: 0 }
  )

  const { handleSubmit, control, reset } = methods

  // TODO: Refactor to one place. See other comments.
  const onSubmit = useCallback(
    async (data: AccessFileFormData) => {
      const actions = []
      const anonymousUserId = getOrCreateAnonymousId()
      const trackingData = {
        formId: 'housingCostReport',
        formData: {
          ...data,
          locale,
        },
      }
      if (!isLoggedIn)
        actions.push(
          sendHousingCostMail({
            email: data.clientEmail,
            locale,
          }),
          trackFormSubmissionServer({
            eventName: 'formSubmission',
            userId,
            anonymousUserId,
            data: trackingData,
          })
        )

      if (data.subscribeNewsletter) {
        const email = isLoggedIn ? session?.email : data.clientEmail
        if (email) {
          actions.push(
            sendNewsletterRegistration({
              email,
              type: NewsletterType.Newsletter,
              locale,
              wantsNewsletter: true,
            }),
            trackFormSubmissionServer({
              eventName: 'formSubmission',
              userId,
              anonymousUserId,
              data: {
                formId: 'Registered for Newsletter',
                formData: {
                  email,
                },
              },
            })
          )
        }
      }

      await Promise.all(actions)

      trackingFormSubmissionForGA4(trackingData)

      !isLoggedIn && show()
      if (isLoggedIn) {
        window.location.href = `${baseUrl}/${jwtHrefData?.href}`
      }
      try {
        reset()
      } catch (error) {}
    },
    [
      isLoggedIn,
      jwtHrefData?.href,
      locale,
      reset,
      sendHousingCostMail,
      sendNewsletterRegistration,
      session?.email,
      show,
      trackFormSubmissionServer,
      trackingFormSubmissionForGA4,
      userId,
    ]
  )

  return (
    <>
      {SuccessDialog}
      <AccessFileForm
        methods={methods}
        handleSubmit={handleSubmit}
        control={control}
        onSubmit={onSubmit}
        loading={somethingIsLoading}
      />
    </>
  )
}
